import React, { useState, useEffect, useMemo } from 'react';
import { useManualSubmit } from '../hooks';
import { Icon } from '../../foundation/primitives';
import { FailOverlay, JudgementModal, MinigameHud, StartGate } from '../../foundation/semantic';

const Template08Chase = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_chase_08', templateId = 8 }) => {
  // R0: ChaseConfig を consume する（T11/14 の configKey 安定化 + T09 の safeNumber ガード）。
  // 既定値は「config 不在時は現行デモと同一挙動」を最優先するため、スキーマ inner default と
  // 異なる現行ハードコード値を採用する（obstacles.speed 8 / obstacles.damage 15 /
  // dash.boost 20 / targetEmoji 🏃‍♂️）。ホストは raw config を渡す（Zod default 充填なし）ため、
  // フィールド不在時は必ずここの既定値が効く。
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const config = hostConfig ?? {};
    const safeNumber = (value, fallback) => (Number.isFinite(value) ? value : fallback);
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const pickText = (value, fallback) => (typeof value === 'string' && value.trim() !== '' ? value : fallback);
    const obstaclesConfig = typeof config.obstacles === 'object' && config.obstacles !== null ? config.obstacles : {};
    const dashConfig = typeof config.dash === 'object' && config.dash !== null ? config.dash : {};
    const successMessage = typeof config.successMessage === 'object' && config.successMessage !== null ? config.successMessage : {};
    const failMessage = typeof config.failMessage === 'object' && config.failMessage !== null ? config.failMessage : {};

    const laneCount = clamp(Math.round(safeNumber(config.laneCount, 3)), 2, 5);
    let goalDistance = safeNumber(config.goalDistance, 100);
    let escapeDistance = safeNumber(config.escapeDistance, 0);
    if (escapeDistance >= goalDistance) {
      // 逆転・同値の不正 config はゲージが成立しないため既定レンジに戻す
      goalDistance = 100;
      escapeDistance = 0;
    }
    // 障害物の降下速度: obstacles.speed > scrollSpeed > 現行値 8 の順で解決。
    // 100ms tick の当たり判定窓（y 70〜90 開区間・幅20）を 1 tick で飛び越えないよう 19 を上限にする。
    const obstacleSpeed = clamp(safeNumber(obstaclesConfig.speed, safeNumber(config.scrollSpeed, 8)), 1, 19);
    const obstacleEmojis = (Array.isArray(obstaclesConfig.types) ? obstaclesConfig.types : [])
      .map((type) => (typeof type?.emoji === 'string' && type.emoji.trim() !== '' ? type.emoji : null))
      .filter((emoji) => emoji !== null);

    return {
      laneCount,
      initialLane: Math.floor(laneCount / 2),
      initialDistance: clamp(safeNumber(config.initialDistance, 50), escapeDistance, goalDistance),
      goalDistance,
      escapeDistance,
      obstacleSpeed,
      obstacleFrequency: clamp(safeNumber(obstaclesConfig.frequency, 0.3), 0, 1),
      obstacleDamage: Math.max(0, safeNumber(obstaclesConfig.damage, 15)),
      obstacleEmojis: obstacleEmojis.length > 0 ? obstacleEmojis : ['🚧'],
      dashEnabled: dashConfig.enabled !== false,
      dashBoost: Math.max(0, safeNumber(dashConfig.boost, 20)),
      dashCooldownMs: Math.max(0, safeNumber(dashConfig.cooldownMs, 2000)),
      playerEmoji: pickText(config.playerEmoji, '🏃'),
      targetEmoji: pickText(config.targetEmoji, '🏃‍♂️'),
      // R1 体験文法: StartGate / MinigameHud の見出しと説明（config 優先・既定は常体の一行）
      title: pickText(config.title, '追跡チェイス'),
      instruction: pickText(config.instructionText, '障害物を避け、距離を詰めて確保せよ'),
      successTitle: pickText(successMessage.title, '確保成功！'),
      successDescription: pickText(successMessage.subtitle, '容疑者を捕まえた'),
      failTitle: pickText(failMessage.title, '逃走された...'),
      failDescription: pickText(failMessage.subtitle, '容疑者を見失った'),
      allowRetry: failMessage.allowRetry !== false
    };
  }, [configKey]);

  const [lane, setLane] = useState(() => resolvedConfig.initialLane);
  const [distance, setDistance] = useState(() => resolvedConfig.initialDistance);
  const [obstacles, setObstacles] = useState([]);
  const [gameState, setGameState] = useState('ready');
  const [dashCooldown, setDashCooldown] = useState(false);
  // R1 体験文法: 開始ゲート。step が showTutorial===false を明示した時だけ最初から開けておく
  const [gateOpen, setGateOpen] = useState(() => hostMeta?.ui?.showTutorial !== false);
  const [retryCount, setRetryCount] = useState(0);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);

  const lanes = useMemo(
    () => Array.from({ length: resolvedConfig.laneCount }, (_, index) => index),
    [resolvedConfig.laneCount]
  );

  // R1 体験文法: 旧スタートボタンの 'ready'→'playing' 遷移はゲート閉鎖に一本化する
  // （showTutorial===false の step ではゲートを出さず即開始＝旧ボタン1タップ分が省かれるだけ）
  useEffect(() => {
    if (gameState === 'ready' && !gateOpen) {
      setGameState('playing');
    }
  }, [gameState, gateOpen]);

  useEffect(() => {
    // R1 体験文法: ? によるゲート再表示中（説明の読み直し）はゲームループも停止する
    if (gameState !== 'playing' || gateOpen) return;

    const gameLoop = setInterval(() => {
      setObstacles(prev => {
        const moved = prev.map(o => ({ ...o, y: o.y + resolvedConfig.obstacleSpeed })).filter(o => o.y < 110);
        const hit = moved.some(o => o.y > 70 && o.y < 90 && o.lane === lane);
        if (hit) {
          setDistance(d => Math.max(resolvedConfig.escapeDistance, d - resolvedConfig.obstacleDamage));
        }
        return moved;
      });

      if (Math.random() < resolvedConfig.obstacleFrequency) {
        const emoji = resolvedConfig.obstacleEmojis[Math.floor(Math.random() * resolvedConfig.obstacleEmojis.length)];
        setObstacles(prev => [
          ...prev,
          { lane: Math.floor(Math.random() * resolvedConfig.laneCount), y: -10, id: Date.now(), emoji }
        ]);
      }

      setDistance(d => {
        const newD = d - 0.5;
        if (newD <= resolvedConfig.escapeDistance) setGameState('fail');
        return newD;
      });
    }, 100);

    return () => clearInterval(gameLoop);
  }, [gameState, lane, resolvedConfig, gateOpen]);

  useEffect(() => {
    if (distance >= resolvedConfig.goalDistance) {
      setGameState('success');
    }
  }, [distance, resolvedConfig.goalDistance]);

  const handleDash = () => {
    if (dashCooldown || gameState !== 'playing') return;
    setDistance(d => Math.min(resolvedConfig.goalDistance, d + resolvedConfig.dashBoost));
    setDashCooldown(true);
    setTimeout(() => setDashCooldown(false), resolvedConfig.dashCooldownMs);
  };

  const reset = () => {
    setLane(resolvedConfig.initialLane);
    setDistance(resolvedConfig.initialDistance);
    setObstacles([]);
    setGameState('ready');
    setDashCooldown(false);
    // R1 体験文法: やり直し時もゲート既定に従う（旧 ready 画面のスタート待ちと等価）
    setGateOpen(hostMeta?.ui?.showTutorial !== false);
    resetSubmission();
  };

  // FailOverlay「やり直す」: 内部リセット + retryCount 加算
  const handleRetry = () => {
    reset();
    setRetryCount((count) => count + 1);
  };

  // 距離ゲージは escape〜goal を 0〜100% に正規化する（既定レンジ 0〜100 では従来の width と同一値）。
  const gaugeRange = resolvedConfig.goalDistance - resolvedConfig.escapeDistance;
  const gaugePercent = Math.min(100, Math.max(0, ((distance - resolvedConfig.escapeDistance) / gaugeRange) * 100));

  return (
    <div className="t08-root relative w-full h-full overflow-hidden">
      <div className="t08-bg-overlay absolute inset-0" />

      {/* R1 体験文法: 共通 HUD はタイトルのみ薄く（距離ゲージは本体の t08-hud に残し二重表示しない）。
          ? は開始ゲート再表示（表示中はゲームループ停止） */}
      <MinigameHud title={resolvedConfig.title} icon="Siren" onHelp={() => setGateOpen(true)} />

      <div className="absolute top-10 left-0 right-0 z-30 p-3">
        <div className="t08-hud rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="t08-hud-label text-xs">距離</span>
            <span className="t08-hud-value text-sm font-mono">{Math.round(distance)}m</span>
          </div>
          <div className="t08-hud-track h-3 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${gaugePercent > 70 ? 't08-hud-fill--safe' : gaugePercent > 30 ? 't08-hud-fill--warn' : 't08-hud-fill--danger'}`}
              style={{ width: `${gaugePercent}%` }}
            />
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="t08-hud-flee">逃走</span>
            <span className="t08-hud-catch">確保</span>
          </div>
        </div>
      </div>

      <div className="absolute top-36 left-0 right-0 bottom-32">
        <div className="relative w-full h-full flex">
          {lanes.map((laneIndex) => (
            <div
              key={laneIndex}
              className={`flex-1 t08-lane relative ${lane === laneIndex ? 't08-lane--active' : ''}`}
            >
              {lane === laneIndex && (
                <div className="t08-runner absolute bottom-16 left-1/2 -translate-x-1/2 w-10 h-14 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">{resolvedConfig.playerEmoji}</span>
                </div>
              )}

              {obstacles.filter(o => o.lane === laneIndex).map((o) => (
                <div
                  key={o.id}
                  className="t08-obstacle absolute left-1/2 -translate-x-1/2 w-12 h-8 rounded flex items-center justify-center"
                  style={{ top: `${o.y}%` }}
                >
                  <span className="text-lg">{o.emoji ?? '🚧'}</span>
                </div>
              ))}
            </div>
          ))}

          <div className="t08-suspect absolute top-4 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full flex items-center justify-center animate-pulse">
            <span className="text-xl">{resolvedConfig.targetEmoji}</span>
          </div>
        </div>
      </div>

      <div className="t08-footer absolute bottom-0 left-0 right-0 z-30 pt-4 pb-6 px-4">
        <div className="flex gap-3">
          <button
            className="t08-move-btn flex-1 font-bold py-4 rounded-xl"
            onClick={() => gameState === 'playing' && setLane(Math.max(0, lane - 1))}
          >
            ◀ 左
          </button>
          {resolvedConfig.dashEnabled && (
            <button
              className={`flex-1 font-bold py-4 rounded-xl flex items-center justify-center gap-1
                ${dashCooldown ? 't08-dash--cooldown' : 't08-dash--ready'}`}
              onClick={handleDash}
              disabled={dashCooldown}
            >
              <Icon name="Zap" size={20} />
              {dashCooldown ? '準備中…' : 'ダッシュ'}
            </button>
          )}
          <button
            className="t08-move-btn flex-1 font-bold py-4 rounded-xl"
            onClick={() => gameState === 'playing' && setLane(Math.min(resolvedConfig.laneCount - 1, lane + 1))}
          >
            右 ▶
          </button>
        </div>
      </div>

      {/* R1 体験文法: 開始ゲート（旧 ready 画面のスタートボタンを置換。表示中はループ停止） */}
      {gateOpen && (
        <StartGate
          title={resolvedConfig.title}
          instruction={resolvedConfig.instruction}
          icon="Siren"
          onStart={() => setGateOpen(false)}
        />
      )}

      {/* Foundation: 確保成功モーダル（中央バッジ🎉 + タイトル + 説明 + 次へ進む を JudgementModal に集約。
          文言は config.successMessage（title/subtitle）を優先し、不在時は現行文言。
          旧: green グラデ円バッジ + 独自オーバーレイ。submit('success') 呼び出しは不変） */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title={resolvedConfig.successTitle}
        description={resolvedConfig.successDescription}
        icon={<span className="text-4xl">🎉</span>}
        buttonLabel="次へ進む"
        onSubmit={() => submit('success', { retryCount })}
      />

      {/* R1 体験文法: 失敗（逃走）を FailOverlay semantic へ移行（旧 .t08-fail-* 独自オーバーレイは撤去）。
          やり直す = reset + retryCount 加算 / 未整理のまま進む = submit('fail')。
          文言は config.failMessage（title/subtitle/allowRetry）を尊重し、不在時は現行既定（常体） */}
      {gameState === 'fail' && (
        <FailOverlay
          title={resolvedConfig.failTitle}
          subtitle={resolvedConfig.failDescription}
          allowRetry={resolvedConfig.allowRetry}
          onRetry={handleRetry}
          onProceed={() => submit('fail', { retryCount })}
        />
      )}
    </div>
  );
};

export default Template08Chase;
